import { defineStore } from 'pinia';
import { fetchSubscriptionData } from '@/utils/subscriptionData';
import { useFeatureFlagsStore } from '@/stores/featureFlags';

/**
 * Mirrors PlanGate::LIMITS feature flags + numeric caps fetched from
 * GET /getusersubscriptiondata. Single in-memory cache so every component
 * that needs to gate UI (nav menu, dashboard tiles, etc.) reads the same
 * source instead of issuing its own request.
 *
 * The shape parallels the API response — we surface what the matrix calls
 * out: countable caps (max_jobs_allowed, max_estimates_allowed, …) and
 * level/flag fields (scheduling_level, has_defects, mat_ai, …). Unknown
 * fields are forgiving on read so an older backend can't break the UI.
 */
interface PlanFlags {
  plan_tiers: string | null;
  bypass_account_limit: boolean;
  // Numeric caps
  max_jobs_allowed: number | null;
  max_estimates_allowed: number | null;
  max_takeoffs_allowed: number | null;
  max_crew_tracker_allowed: number | null;
  max_file_usage_formatted: string | null;
  // Feature levels / flags
  scheduling_level: 'none' | 'basic' | 'gantt' | 'gantt_weather' | null;
  has_defects: boolean | null;
  has_reporting: boolean | null;
  mat_ai: 'none' | 'limited' | 'unlimited' | null;
  has_client_portal: boolean | null;
  has_ohs_module: boolean | null;
  mobile_app_level: 'calculators_only' | 'full' | null;
  has_ads: boolean | null;
  support_level: string | null;
  // Admin-set per-company feature toggles (default off for everyone). Drive
  // the hide of Mat AI / Smart Quote via the featureFlags store — see load().
  feature_mat_ai: boolean | null;
  feature_smart_quote: boolean | null;
}

interface PlanAccessState {
  flags: PlanFlags | null;
  loaded: boolean;
  loading: Promise<void> | null;
}

const EMPTY_FLAGS: PlanFlags = {
  plan_tiers: null,
  bypass_account_limit: false,
  max_jobs_allowed: null,
  max_estimates_allowed: null,
  max_takeoffs_allowed: null,
  max_crew_tracker_allowed: null,
  max_file_usage_formatted: null,
  scheduling_level: null,
  has_defects: null,
  has_reporting: null,
  mat_ai: null,
  has_client_portal: null,
  has_ohs_module: null,
  mobile_app_level: null,
  has_ads: null,
  support_level: null,
  feature_mat_ai: null,
  feature_smart_quote: null,
};

export const usePlanAccessStore = defineStore('planAccess', {
  state: (): PlanAccessState => ({
    flags: null,
    loaded: false,
    loading: null,
  }),

  getters: {
    // Fall back to "allowed" if flags aren't loaded yet — better to flash
    // the menu open than to hide everything during the initial fetch.
    isLoaded: (state) => state.loaded,
    plan: (state) => state.flags?.plan_tiers ?? null,
    bypassAll: (state) => state.flags?.bypass_account_limit === true,

    /**
     * Method-style getter — returns a function. Using a getter (not an
     * action) so Vue's reactivity inside <NavItem>'s `computed isLocked`
     * tracks `state.flags` correctly: change `flags`, computed re-evaluates,
     * template re-renders. Actions don't necessarily propagate reactivity
     * the same way when called from a template computed.
     */
    isAllowed: (state) => (feature: string): boolean => {
      const f = state.flags;
      if (!f) return true;                       // pre-load: permissive
      if (f.bypass_account_limit === true) return true; // admin/legacy bypass

      switch (feature) {
        case 'projects':
          return Number(f.max_jobs_allowed ?? 0) > 0;
        case 'estimates':
          return Number(f.max_estimates_allowed ?? 0) > 0;
        case 'takeoffs':
          return Number(f.max_takeoffs_allowed ?? 0) > 0;
        case 'crew_tracker':
          return Number(f.max_crew_tracker_allowed ?? 0) > 0;
        case 'storage': {
          const m = String(f.max_file_usage_formatted ?? '').trim();
          return !(m === '0B' || m === '0 B' || m === '0' || m === '');
        }
        case 'scheduling':
          return (f.scheduling_level ?? 'none') !== 'none';
        case 'defects':
          return f.has_defects === true;
        case 'reporting':
          return f.has_reporting === true;
        case 'mat_ai':
          return (f.mat_ai ?? 'none') !== 'none';
        case 'client_portal':
          return f.has_client_portal === true;
        case 'quotes':
          // Quotes is locked on the free plan only; every paid plan keeps access.
          // Plan-name based (not a has_* flag) because the gate is specifically
          // "not Freemium", not the client_portal feature flag.
          return (f.plan_tiers ?? '') !== 'Freemium';
        case 'ohs_module':
          return f.has_ohs_module === true;
        default:
          return true;
      }
    },
  },

  actions: {
    async load(userId: number | string): Promise<void> {
      if (this.loaded) return;
      if (this.loading) return this.loading;

      // Shared with the router's payment gate and the profile menu, which ask
      // the same endpoint for the same user on the same pageload. Going through
      // utils/subscriptionData means whichever of the three runs first pays for
      // the request and the other two read its answer — see the note there, and
      // Sentry BS-WEB-APP-16P for what it looked like when they didn't.
      this.loading = (async () => {
        try {
          const body: any = await fetchSubscriptionData(userId);
          this.flags = { ...EMPTY_FLAGS, ...(body || {}) };
          this.loaded = true;
          // Feed the admin feature toggles into the featureFlags store so Mat
          // AI / Smart Quote hide via the same machinery as env flags.
          useFeatureFlagsStore().applyUserFlags(this.flags);
        } catch {
          // On failure leave flags null + loaded=false so subsequent reads
          // return the permissive default. The router/restrictions logic
          // independently gates dangerous actions (cf. subscriptionAccess.ts).
          // Mat AI / Smart Quote fail CLOSED (hidden) — they're opt-in per
          // account, so an errored fetch must not reveal them.
          this.flags = null;
          this.loaded = false;
          useFeatureFlagsStore().applyUserFlags(null);
        } finally {
          this.loading = null;
        }
      })();
      return this.loading;
    },

    reset() {
      this.flags = null;
      this.loaded = false;
      this.loading = null;
    },
  },
});
